import { useState, useEffect } from 'react';
import { Download, Play } from 'lucide-react';
import type { ContentWidgetData, PlatformContent, ContentLink } from '@/types';
import { getUserContentLinks } from '@/services/contentLinks';
import { getUserContents } from '@/services/instagram';

interface ContentWidgetProps {
  data: ContentWidgetData;
  userId?: string;
}

const ContentWidget = ({ data, userId }: ContentWidgetProps) => {
  const [items, setItems] = useState<Array<{ content: PlatformContent; link: ContentLink }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [links, contents] = await Promise.all([
          getUserContentLinks(userId),
          getUserContents(userId),
        ]);

        const contentMap = new Map(contents.map((c) => [c.id, c]));
        const joined = links
          .filter((l) => l.status === 'active')
          .map((link) => {
            const content = contentMap.get(link.contentId);
            return content ? { content, link } : null;
          })
          .filter((item): item is { content: PlatformContent; link: ContentLink } => item !== null)
          .slice(0, data.maxItems || 6);

        setItems(joined);
      } catch (err) {
        console.error('Failed to load content widget:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, data.maxItems]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-soft dark:shadow-none p-4">
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: Math.min(data.maxItems || 6, 6) }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-gray-100 dark:bg-dark-700 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-soft dark:shadow-none p-4">
      {data.title && (
        <h3 className="font-semibold text-dark-800 dark:text-white mb-3 text-sm">{data.title}</h3>
      )}
      <div className="grid grid-cols-3 gap-2">
        {items.map(({ content, link }) => (
          <a
            key={link.id}
            href={`https://klipr.it/${link.code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="aspect-square rounded-xl overflow-hidden relative group"
          >
            {content.thumbnailUrl || content.storageUrl ? (
              <img
                src={content.thumbnailUrl || content.storageUrl || ''}
                alt={content.caption || ''}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                <Download className="w-6 h-6 text-gray-300 dark:text-gray-600" />
              </div>
            )}
            {content.mediaType === 'VIDEO' && (
              <div className="absolute top-1.5 right-1.5">
                <Play className="w-3.5 h-3.5 text-white drop-shadow-md fill-white" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Download className="w-5 h-5 text-white" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default ContentWidget;
